import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { Cart, CartItem, Product } from '../types';
import { logout } from './authSlice';
import { extractApiErrorMessage } from '../utils/errorHandler';

const GUEST_CART_KEY = 'vediluxe_guest_cart';

const loadGuestCart = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveGuestCart = (items: CartItem[]) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
};

interface CartState {
  cart: Cart | null;
  guestItems: CartItem[];
  loading: boolean;
  mutating: boolean;
  error: string | null;
  miniCartOpen: boolean;
}

const initialState: CartState = {
  cart: null,
  guestItems: loadGuestCart(),
  loading: false,
  mutating: false,
  error: null,
  miniCartOpen: false
};

interface AddToCartPayload {
  productId: string;
  quantity?: number;
}

interface UpdateCartItemPayload {
  productId: string;
  quantity: number;
}

export const fetchMyCart = createAsyncThunk<Cart, void, { rejectValue: string }>(
  'cart/fetchMyCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.CART}`);
      return response.data as Cart;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to load cart'));
    }
  }
);

export const addToCart = createAsyncThunk<Cart, AddToCartPayload, { rejectValue: string }>(
  'cart/addToCart',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.CART}/items`, {
        productId: payload.productId,
        quantity: payload.quantity || 1
      });
      return response.data as Cart;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to add item to cart'));
    }
  }
);

export const updateCartItem = createAsyncThunk<Cart, UpdateCartItemPayload, { rejectValue: string }>(
  'cart/updateCartItem',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`${API_ENDPOINTS.CART}/items`, payload);
      return response.data as Cart;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to update cart item'));
    }
  }
);

export const removeCartItem = createAsyncThunk<Cart, { productId: string }, { rejectValue: string }>(
  'cart/removeCartItem',
  async ({ productId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`${API_ENDPOINTS.CART}/items`, {
        data: { productId }
      });
      return response.data as Cart;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to remove cart item'));
    }
  }
);

export const clearMyCart = createAsyncThunk<Cart, void, { rejectValue: string }>(
  'cart/clearMyCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.CART}/clear`);
      return response.data as Cart;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to clear cart'));
    }
  }
);

/** Sync guest cart items to server cart after login */
export const syncGuestCart = createAsyncThunk<Cart | null, void, { rejectValue: string }>(
  'cart/syncGuestCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { cart: CartState };
      const guestItems = state.cart.guestItems;
      if (guestItems.length === 0) return null;

      // Add each guest item to the server cart
      let latestCart: Cart | null = null;
      for (const item of guestItems) {
        const response = await apiClient.post(`${API_ENDPOINTS.CART}/items`, {
          productId: item.product._id,
          quantity: item.quantity
        });
        latestCart = response.data as Cart;
      }
      return latestCart;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Failed to sync cart'));
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    closeMiniCart(state) {
      state.miniCartOpen = false;
    },
    addToGuestCart(state, action: PayloadAction<{ product: Product; quantity: number }>) {
      const { product, quantity } = action.payload;
      const existing = state.guestItems.find((item) => item.product._id === product._id);
      if (existing) {
        existing.quantity += quantity;
      } else {
        state.guestItems.push({ product, quantity });
      }
      saveGuestCart(state.guestItems);
      state.miniCartOpen = true;
    },
    updateGuestCartItem(state, action: PayloadAction<{ productId: string; quantity: number }>) {
      const item = state.guestItems.find((i) => i.product._id === action.payload.productId);
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
        saveGuestCart(state.guestItems);
      }
    },
    removeGuestCartItem(state, action: PayloadAction<{ productId: string }>) {
      state.guestItems = state.guestItems.filter(
        (item) => item.product._id !== action.payload.productId
      );
      saveGuestCart(state.guestItems);
    },
    clearGuestCart(state) {
      state.guestItems = [];
      localStorage.removeItem(GUEST_CART_KEY);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cart = action.payload;
      })
      .addCase(fetchMyCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to load cart';
      })
      .addCase(addToCart.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.mutating = false;
        state.cart = action.payload;
        state.miniCartOpen = true;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to add item to cart';
      })
      .addCase(updateCartItem.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.mutating = false;
        state.cart = action.payload;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to update cart item';
      })
      .addCase(removeCartItem.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        state.mutating = false;
        state.cart = action.payload;
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to remove cart item';
      })
      .addCase(clearMyCart.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(clearMyCart.fulfilled, (state, action) => {
        state.mutating = false;
        state.cart = action.payload;
      })
      .addCase(clearMyCart.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload || action.error.message || 'Failed to clear cart';
      })
      .addCase(syncGuestCart.fulfilled, (state, action) => {
        if (action.payload) {
          state.cart = action.payload;
        }
        state.guestItems = [];
        localStorage.removeItem(GUEST_CART_KEY);
      })
      .addCase(logout, (state) => {
        state.cart = null;
        state.loading = false;
        state.mutating = false;
        state.error = null;
      });
  }
});

export const { closeMiniCart, addToGuestCart, updateGuestCartItem, removeGuestCartItem, clearGuestCart } = cartSlice.actions;
export default cartSlice.reducer;
